import type { MarketingIndustrySlug } from './industry-page-model';
import type { MarketingIndustryPageSource } from './industry-page-content.types';

type MiddleIndustrySlug = Extract<
  MarketingIndustrySlug,
  'packaging' | 'furniture' | 'beauty-personal-care'
>;

export const MARKETING_INDUSTRY_PAGE_CONTENT_B = {
  packaging: {
    heroBody:
      'Explore structures, materials, and artwork. Adapt every SKU and market before samples, plates, or tooling.',
    categories: [
      'Flexible',
      'Rigid',
      'Folding Carton',
      'Corrugated',
      'Labels',
      'Protective & Transit',
    ],
    benefitsHeading: 'What packaging teams gain',
    benefits: [
      {
        title: 'Fewer development rounds',
        body: 'Compare formats, materials, and graphics before commissioning physical samples.',
      },
      {
        title: 'Fewer artwork errors',
        body: 'Connect every size, flavor, language, claim, and barcode to the correct pack.',
      },
      {
        title: 'Better packaging decisions',
        body: 'Balance protection, production, cost, logistics, and material use.',
      },
    ],
    workflowHeading: 'From brief to production',
    workflowSteps: [
      {
        number: '01',
        title: 'Define the Brief',
        body: 'Capture the product, fill conditions, shelf life, channel, target cost, markets, and sustainability requirements.',
      },
      {
        number: '02',
        title: 'Explore the Pack System',
        body: 'Develop primary, secondary, and transit-pack directions across structures, materials, closures, and graphics.',
      },
      {
        number: '03',
        title: 'Build the Pack Family',
        body: 'Create sizes, SKUs, flavors, languages, claims, barcodes, and market variants from the selected design.',
      },
      {
        number: '04',
        title: 'Prepare and Validate',
        body: 'Organize specifications, dielines, materials, tolerances, tests, line requirements, and pallet information.',
      },
      {
        number: '05',
        title: 'Approve and Launch',
        body: 'Connect comments, approvals, and final files to pack visuals, retailer assets, product pages, and campaigns.',
      },
    ],
    capabilitiesHeading: 'One product. A complete packaging system.',
    capabilities: [
      {
        title: 'Primary pack',
        body: 'Connect product protection, structure, material, closures, artwork, and customer information.',
      },
      {
        title: 'Secondary pack',
        body: 'Coordinate shelf presentation, SKU variants, claims, barcodes, and retail requirements.',
      },
      {
        title: 'Transit and distribution',
        body: 'Carry handling, shipping, protection, pack weight, pallet, and load requirements forward.',
      },
      {
        title: 'Digital layer',
        body: 'Keep QR codes, market content, product pages, retailer assets, and campaigns aligned.',
      },
    ],
    systemsHeading: 'Keep your packaging systems',
    systemsBody:
      'Continue using structural CAD, artwork, prepress, PLM, ERP/MIS, QMS, laboratory, palletization, PIM, and DAM systems. IMAI connects creation and packaging decisions around them.',
    recordHeading: 'Or make My Drive the complete packaging record',
    recordBody:
      'Keep briefs, structures, materials, SKUs, claims, barcodes, tests, evidence, approvals, specifications, suppliers, logistics, and channel content together.',
    faq: {
      label: 'Frequently Asked Questions',
      heading: 'FAQ',
      items: [
        {
          question: 'What packaging formats can IMAI support?',
          answer:
            'Flexible, rigid, carton, corrugated, label, protective, and transit packaging.',
        },
        {
          question: 'Does IMAI replace CAD or prepress?',
          answer:
            'No. IMAI supports concepts, variants, and handoff. Final structures and print files remain in specialist systems.',
        },
        {
          question: 'Can it manage multiple SKUs and markets?',
          answer:
            'Yes. Sizes, languages, claims, barcodes, and artwork versions remain connected to the correct pack.',
        },
        {
          question: 'Can it support sustainability requirements?',
          answer:
            'Yes. Teams can organize material, weight, recycled-content, recyclability, reuse, and compliance information.',
        },
      ],
    },
    cta: {
      eyebrow: 'Structure, artwork, variants, and validation connected',
      heading: 'Build the complete pack before production.',
      cards: [
        { label: 'Build a Packaging System', href: '/auth?mode=signup' },
        { label: 'Book a Packaging Demo', href: '/contact' },
      ],
    },
  },
  furniture: {
    heroBody:
      'Turn one furniture concept into a development-ready product family with every size, module, material, and finish connected.',
    categories: [
      'Upholstery',
      'Seating',
      'Casegoods',
      'Tables',
      'Bedroom',
      'Office & Contract',
    ],
    benefitsHeading: 'See the complete product before you build it',
    benefits: [
      {
        title: 'Reduce prototype rounds',
        body: 'Resolve form, proportion, materials, and configurations before physical development.',
      },
      {
        title: 'Protect cost and margin',
        body: 'See how product changes affect components, material consumption, and costing inputs.',
      },
      {
        title: 'Sell every configuration',
        body: 'Create product views, room scenes, and commerce content from the approved design.',
      },
    ],
    workflowHeading: 'From product idea to the customer’s room',
    workflowSteps: [
      {
        number: '01',
        title: 'Define the Product',
        body: 'Set the user, space, dimensions, price target, materials, and performance requirements.',
        output: 'Connected product brief',
      },
      {
        number: '02',
        title: 'Design the Product Family',
        body: 'Explore form, proportion, sizes, modules, upholstery, finishes, legs, and hardware.',
        output: 'Approved design and configuration family',
      },
      {
        number: '03',
        title: 'Prepare for Development',
        body: 'Organize dimensions, components, BOM inputs, target cost, assembly, packaging, and testing requirements.',
        output: 'Development-ready product record',
      },
      {
        number: '04',
        title: 'Launch Every Version',
        body: 'Create product imagery, details, finish grids, room scenes, specification sheets, pages, and video.',
        output: 'Channel-ready content',
      },
    ],
    capabilitiesHeading: 'Furniture-specific capabilities',
    capabilities: [
      {
        title: 'Design and configuration',
        body: 'Explore form, proportion, size, modules, components, hardware, upholstery, and finishes.',
      },
      {
        title: 'Materials and cost',
        body: 'Connect wood, metal, glass, fabrics, leather, foam, filling, consumption, and costing inputs.',
      },
      {
        title: 'Development readiness',
        body: 'Organize product views, exploded views, BOMs, specifications, assembly, packaging, and tests.',
      },
      {
        title: 'Room and commerce',
        body: 'Create in-room visualization, finish imagery, dealer materials, product pages, and campaigns.',
      },
    ],
    systemsHeading: 'Keep your furniture systems',
    systemsBody:
      'Continue using CAD, engineering, PLM, ERP/MRP, configuration, PIM, DAM, and commerce systems while IMAI connects creative and product decisions.',
    recordHeading: 'Or make My Drive the complete system of record',
    recordBody:
      'Keep product architecture, designs, configurations, materials, costs, samples, suppliers, tests, assembly, packaging, imagery, care, repair, and product-passport records together.',
    faq: {
      label: 'Frequently Asked Questions',
      heading: 'FAQ',
      items: [
        {
          question: 'Can IMAI manage modular furniture?',
          answer:
            'Yes. Shared modules, components, and materials can be organized while every approved configuration remains identifiable.',
        },
        {
          question: 'Does IMAI replace furniture CAD or engineering?',
          answer:
            'No. IMAI supports design, configuration, product information, and handoff. Detailed engineering remains in specialist systems.',
        },
        {
          question: 'Can it show the cost impact of changes?',
          answer:
            'IMAI connects configurations, materials, components, and costing inputs for earlier comparison. Manufacturing partners confirm final costs.',
        },
        {
          question: 'Can approved designs create selling content?',
          answer:
            'Yes. Use them for product views, room scenes, finish grids, specification sheets, e-commerce content, and campaigns.',
        },
      ],
    },
    cta: {
      eyebrow: 'One design. Every configuration.',
      heading: 'Build the collection before the first prototype.',
      cards: [
        { label: 'Build a Furniture Collection', href: '/auth?mode=signup' },
        { label: 'Book a Furniture Demo', href: '/contact' },
      ],
    },
  },
  'beauty-personal-care': {
    heroBody:
      'Turn consumer signals into product concepts, formula briefs, packaging directions, variants, and launch content.',
    categories: [
      'Skincare',
      'Makeup',
      'Haircare',
      'Fragrance',
      'Bodycare',
      'Personal Care',
    ],
    benefitsHeading: 'From consumer need to product line',
    benefits: [
      {
        title: 'Find stronger opportunities',
        body: 'Turn consumer behavior, reviews, and unmet needs into differentiated directions.',
      },
      {
        title: 'Build complete lines faster',
        body: 'Develop products, routines, shades, scents, sizes, and packaging around one opportunity.',
      },
      {
        title: 'Launch defensible claims',
        body: 'Connect every product promise to approved evidence and the correct product version.',
      },
    ],
    workflowHeading: 'From opportunity to launch',
    workflowSteps: [
      {
        number: '01',
        title: 'Define the Consumer Need',
        body: 'Capture the consumer, routine, market, price, channel, benchmark, and intended benefit.',
      },
      {
        number: '02',
        title: 'Build the Product Line',
        body: 'Explore formats, textures, ingredient directions, shades, scents, and complementary products.',
      },
      {
        number: '03',
        title: 'Develop Product and Packaging',
        body: 'Organize formula briefs, samples, packaging components, applicators, costs, and market variants.',
      },
      {
        number: '04',
        title: 'Test and Approve',
        body: 'Connect stability, microbiological, safety, compatibility, efficacy, and consumer-test results to each version.',
      },
      {
        number: '05',
        title: 'Launch Every Market',
        body: 'Organize ingredients, warnings, claims, artwork, and market records, then create channel-ready content.',
      },
    ],
    capabilitiesHeading: 'The formula, pack, and promise must match',
    capabilities: [
      {
        title: 'Consumer insight',
        body: 'Connect unmet needs, routines, markets, price points, channels, and intended benefits.',
      },
      {
        title: 'Formula direction',
        body: 'Organize formats, textures, ingredient directions, shades, scents, samples, and variants.',
      },
      {
        title: 'Packaging and evidence',
        body: 'Keep compatibility, stability, safety, efficacy, artwork, and approval records with the product.',
      },
      {
        title: 'Market launch',
        body: 'Carry approved ingredients, warnings, claims, pages, campaigns, creator briefs, and video forward.',
      },
    ],
    systemsHeading: 'Keep your beauty and regulatory systems',
    systemsBody:
      'Continue using formulation, LIMS, PLM, ERP, QMS, regulatory, artwork, PIM, DAM, and commerce systems. Qualified chemists and specialists retain approval responsibility.',
    recordHeading: 'Or use My Drive as the complete product record',
    recordBody:
      'Keep consumer insights, formula briefs, variants, packaging, samples, tests, safety records, evidence, approvals, market records, launch content, and feedback connected.',
    faq: {
      label: 'Frequently Asked Questions',
      heading: 'FAQ',
      items: [
        {
          question: 'Can IMAI create cosmetic formulas?',
          answer:
            'IMAI supports ingredient research and formula briefs. Qualified cosmetic chemists formulate and approve the product.',
        },
        {
          question: 'Can it manage shades, scents, and variants?',
          answer:
            'Yes. Every shade, scent, size, package, and market version can remain connected to the correct product record.',
        },
        {
          question: 'Can IMAI validate safety or claims?',
          answer:
            'No. It organizes requirements and evidence. Qualified specialists perform testing and claim substantiation.',
        },
        {
          question: 'Can approved products create launch content?',
          answer:
            'Yes. Use approved product information for retailers, marketplaces, education, campaigns, creator briefs, and videos.',
        },
      ],
    },
    cta: {
      eyebrow: 'Bring one live product brief',
      heading: 'Build the complete line around the next beauty need.',
      cards: [
        { label: 'Run Your Product Brief', href: '/auth?mode=signup' },
        { label: 'Book a Beauty Demo', href: '/contact' },
      ],
    },
  },
} as const satisfies Record<MiddleIndustrySlug, MarketingIndustryPageSource>;
